import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { settingsAPI } from '../../../api';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { FormLabel } from '../../../components/ui/FormLabel';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/Card';
import { ArrowLeft } from 'lucide-react';
import { useToast } from '../../../hooks/useToast';

export default function AssetsSettings() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settingsSnapshot, setSettingsSnapshot] = useState(null);
  const [vatRegistrationDate, setVatRegistrationDate] = useState('');
  const [companyVatNo, setCompanyVatNo] = useState('');

  useEffect(() => {
    settingsAPI
      .get()
      .then((res) => {
        const s = res.settings || res;
        setSettingsSnapshot(s);
        setVatRegistrationDate(s.vat_registration_date || '');
        setCompanyVatNo(s.company_vat_no || '');
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!settingsSnapshot) return;
    try {
      setSaving(true);
      await settingsAPI.update({
        ...settingsSnapshot,
        vat_registration_date: vatRegistrationDate || null,
        company_vat_no: companyVatNo || null,
      });
      addToast({ type: 'success', message: 'Settings saved' });
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button variant="ghost" onClick={() => navigate('/tenant/assets/reports')} className="mb-4 -ml-2">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Reports
        </Button>

        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Assets & Stock settings</h1>

        <Card className="bg-white dark:bg-gray-800">
          <CardHeader>
            <CardTitle className="text-gray-900 dark:text-gray-100">VAT pre-registration report</CardTitle>
            <CardDescription className="text-gray-600 dark:text-gray-400">
              Your VAT registration date defines the end of the pre-registration purchase window (up to 4 years before).
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
            ) : (
              <form onSubmit={handleSave} className="space-y-4">
                <div>
                  <FormLabel htmlFor="companyVatNo">Company VAT number</FormLabel>
                  <Input
                    id="companyVatNo"
                    value={companyVatNo}
                    onChange={(e) => setCompanyVatNo(e.target.value)}
                    className="border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="GB123456789"
                  />
                </div>
                <div>
                  <FormLabel htmlFor="vatRegistrationDate">VAT registration date</FormLabel>
                  <Input
                    id="vatRegistrationDate"
                    type="date"
                    value={vatRegistrationDate}
                    onChange={(e) => setVatRegistrationDate(e.target.value)}
                    className="border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <Button type="submit" disabled={saving}>
                  {saving ? 'Saving…' : 'Save settings'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
